import Skeleton from "Components/Common/Skeleton/Skeleton";
import { formatDate } from "utils/Common/Date";

interface Props {
  stakeholders: any;
  loading: boolean;
  preConstructionStatus?: any;
}

const PhoneIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path
      d="M2 1h2.5l1 2.5-1.5 1a6 6 0 002.5 2.5l1-1.5 2.5 1V10a1 1 0 01-1 1C4.5 11 0 6.5 0 2a1 1 0 011-1h1z"
      stroke="var(--tx3)"
      strokeWidth="0.9"
      fill="none"
    />
  </svg>
);
const CircleIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <circle
      cx="5.5"
      cy="5.5"
      r="4.5"
      stroke="var(--tx3)"
      strokeWidth="1"
    ></circle>
  </svg>
);
const MailIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 9" fill="none">
    <rect
      x="0.5"
      y="2"
      width="10"
      height="7"
      rx="1"
      stroke="var(--tx3)"
      strokeWidth="0.9"
      fill="none"
    />
    <path
      d="M1 2.5l4.5 3.5L10 2.5"
      stroke="var(--tx3)"
      strokeWidth="0.9"
      fill="none"
    />
  </svg>
);

const PinIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path
      d="M5.5 1a3 3 0 013 3c0 2.5-3 6-3 6S2.5 6.5 2.5 4a3 3 0 013-3z"
      stroke="var(--tx3)"
      strokeWidth="0.9"
      fill="none"
    />
    <circle
      cx="5.5"
      cy="4"
      r="1"
      stroke="var(--tx3)"
      strokeWidth="0.9"
      fill="none"
    />
  </svg>
);

function ContactsSkeleton() {
  return (
    <div>
      <Skeleton width={140} height={18} style={{ marginBottom: 6 }} />
      <Skeleton width={200} height={12} style={{ marginBottom: 20 }} />
      {[1, 2, 3, 4].map((i) => (
        <div key={i} style={{ marginBottom: 20 }}>
          <Skeleton width={120} height={11} style={{ marginBottom: 10 }} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              background: "#fff",
              border: "0.5px solid var(--bd)",
              borderRadius: 10,
              padding: 16,
            }}
          >
            {[0, 1].map((j) => (
              <div key={j}>
                <Skeleton width={80} height={10} style={{ marginBottom: 6 }} />
                <Skeleton width={130} height={14} style={{ marginBottom: 8 }} />
                <Skeleton width={150} height={11} style={{ marginBottom: 5 }} />
                <Skeleton width={110} height={11} style={{ marginBottom: 5 }} />
                <Skeleton width={160} height={11} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const fullName = (obj: any) =>
  `${obj?.firstName ?? ""} ${obj?.lastName ?? ""}`.trim() || "—";

export default function ContactsSection({
  stakeholders: s,
  loading,
  preConstructionStatus,
}: Props) {
  if (loading) return <ContactsSkeleton />;

  if (!s) {
    return (
      <div>
        <div className="section-title">Contacts &amp; Lender</div>
        <div className="section-sub">No contacts available.</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="sectionTitle">Contacts &amp; Lender</h1>
      <div className="section-sub">
        All parties associated with this project
      </div>

      {/* BANK / LENDER */}
      {(s.lender?.bank || s.lender?.officer) && (
        <div className="cg-group">
          <div className="cg-group-title">Bank / Lender</div>
          <div className="cg-group-body">
            {s.lender?.bank && (
              <div className="contact-card">
                <div className="contact-role">Primary Bank</div>
                <div className="contact-name">
                  {s.lender.bank.companyName || s.lender.bank.branchName || "—"}
                </div>
                <div className="contact-details">
                  {s.lender.bank.address && (
                    <div className="contact-detail">
                      <PinIcon />
                      {s.lender.bank.address}
                    </div>
                  )}
                  {s.lender.officer && (
                    <div className="contact-detail">
                      <CircleIcon /> Lending Officer:{" "}
                      {s.lender.officer.firstName && s.lender.officer.firstName}{" "}
                      {s.lender.officer.lastName && s.lender.officer.lastName}
                    </div>
                  )}
                  {s.lender.bank.email && (
                    <div className="contact-detail">
                      <MailIcon />{" "}
                      {[
                        s.lender.bank.city,
                        s.lender.bank.state,
                        s.lender.bank.zipCode,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>
              </div>
            )}
            {s.lender?.officer && (
              <div className="contact-card">
                <div className="contact-role">Lending Officer</div>
                <div className="contact-name">{fullName(s.lender.officer)}</div>
                <div className="contact-details">
                  {s.lender.officer.phone && (
                    <div className="contact-detail">
                      <PhoneIcon /> {s.lender.officer.phone}
                    </div>
                  )}
                  {s.lender.officer.email && (
                    <div className="contact-detail">
                      <MailIcon /> {s.lender.officer.email}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* OWNER & REQUISITION */}
      {(s.owner || s.requisitionSigner) && (
        <div className="cg-group">
          <div className="cg-group-title">Owner &amp; Requisition</div>
          <div className="cg-group-body">
            {s.owner && (
              <div className="contact-card">
                <div className="contact-role">Owner</div>
                <div className="contact-name">
                  {s.owner.companyName || fullName(s.owner)}
                </div>
                <div className="contact-details">
                  {s.owner.companyName && (
                    <div className="contact-detail">
                      Primary Contact: {fullName(s.owner)}
                    </div>
                  )}
                  {s.owner.phone && (
                    <div className="contact-detail">
                      <PhoneIcon /> {s.owner.phone}
                    </div>
                  )}
                  {s.owner.address && (
                    <div className="contact-detail">
                      <PinIcon /> {s.owner.address}
                    </div>
                  )}
                </div>
              </div>
            )}
            {s.requisitionSigner && (
              <div className="contact-card">
                <div className="contact-role">Requisition Signer</div>
                <div className="contact-name">
                  {s.requisitionSigner.name ||
                    fullName(s.requisitionSigner) ||
                    "—"}
                </div>
                <div className="contact-details">
                  <div className="contact-detail">
                    Signs all requisition applications
                  </div>
                  {s.requisitionSigner.primaryPhone && (
                    <div className="contact-detail">
                      <PhoneIcon />{" "}
                      {s.requisitionSigner.primaryPhone ??
                        s.requisitionSigner.phone}
                    </div>
                  )}

                  {s.requisitionSigner.email && (
                    <div className="contact-detail">
                      <MailIcon /> {s.requisitionSigner.email}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GENERAL CONTRACTOR */}
      {(s.gc || s.projectManager) && (
        <div className="cg-group">
          <div className="cg-group-title">General Contractor</div>
          <div className="cg-group-body">
            {s.gc && (
              <div className="contact-card">
                <div className="contact-role">General Contractor</div>
                <div className="contact-name">
                  {s.gc.companyName || fullName(s.gc)}
                </div>
                <div className="contact-details">
                  {s.gc.companyName && (
                    <div className="contact-detail">
                      Contact: {fullName(s.gc)}
                    </div>
                  )}
                  {s.gc.address && (
                    <div className="contact-detail">
                      <PinIcon /> {s.gc.address}
                    </div>
                  )}
                  {s.gc.phone && (
                    <div className="contact-detail">
                      <PhoneIcon /> {s.gc.phone}
                    </div>
                  )}
                  {s.gc.email && (
                    <div className="contact-detail">
                      <MailIcon /> {s.gc.email}
                    </div>
                  )}
                </div>
              </div>
            )}
            {preConstructionStatus && (
              <div className="contact-card">
                <div className="contact-role">Pre-Construction Status</div>
                <div className="contact-name">
                  {preConstructionStatus.checklistType}
                </div>
                <div className="contact-details" style={{ marginTop: 6 }}>
                  <div>
                    <span
                      className={`badge ${preConstructionStatus.status?.toLowerCase().includes("review") ? "b-review" : "b-active"}`}
                    >
                      <span className="bdot" />
                      {preConstructionStatus.status}
                    </span>
                  </div>
                  {preConstructionStatus.submittedAt && (
                    <div className="contact-detail" style={{ marginTop: 4 }}>
                      Submitted{" "}
                      {formatDate(preConstructionStatus.submittedAt, false)}
                      {(preConstructionStatus.docCount != null ||
                        preConstructionStatus.qaCount != null) && (
                        <>
                          {" "}
                          · {preConstructionStatus.docCount ?? 0} docs +{" "}
                          {preConstructionStatus.qaCount ?? 0} Q&amp;A
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* OTHER PARTIES */}
      {(s.inspectors?.length ||
        s.othersOnCc ||
        s.architect ||
        s.admin ||
        s.projectManager) && (
        <div className="cg-group">
          <div className="cg-group-title">Other Parties</div>
          <div className="cg-group-body">
            {s.inspectors?.map((ins: any, i: number) => (
              <div key={i} className="contact-card">
                <div className="contact-role">
                  CSC Inspector{ins.role ? ` (${ins.role})` : ""}
                </div>
                <div className="contact-name">{fullName(ins)}</div>
                <div className="contact-details">
                  {ins.address && (
                    <div className="contact-detail">
                      <PinIcon /> {ins.address}
                    </div>
                  )}
                  {ins.phone && (
                    <div className="contact-detail">
                      <PhoneIcon /> {ins.phone}
                    </div>
                  )}
                  {ins.email && (
                    <div className="contact-detail">
                      <MailIcon /> {ins.email}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {s.othersOnCc && (
              <div className="contact-card">
                <div className="contact-role">Others CC'd on Reports</div>
                {s.othersOnCc && (
                  <div className="contact-detail">
                    <MailIcon /> {s.othersOnCc.join(", ")}
                  </div>
                )}
              </div>
            )}
            {s.architect && (
              <div className="contact-card">
                <div className="contact-role">Architect</div>
                <div className="contact-name">{fullName(s.architect)}</div>
                <div className="contact-details">
                  {s.architect.phone && (
                    <div className="contact-detail">
                      <PhoneIcon /> {s.architect.phone}
                    </div>
                  )}
                  {s.architect.email && (
                    <div className="contact-detail">
                      <MailIcon /> {s.architect.email}
                    </div>
                  )}
                </div>
              </div>
            )}
            {s.projectManager && (
              <div className="contact-card">
                <div className="contact-role">Project Manager</div>
                <div className="contact-name">{fullName(s.projectManager)}</div>
                <div className="contact-details">
                  {(s.projectManager.primaryContactPhone ||
                    s.projectManager.phone) && (
                    <div className="contact-detail">
                      <PhoneIcon />{" "}
                      {s.projectManager.primaryContactPhone ||
                        s.projectManager.phone}
                    </div>
                  )}
                  {s.projectManager.email && (
                    <div className="contact-detail">
                      <MailIcon /> {s.projectManager.email}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
